<template>
  <div aria-label="community details" class="community-details">
    <h1>
      <span class="community-details-title" v-if="headerData.community_name">{{
        headerData.community_name
      }}</span>
      <span class="community-details-text" v-if="!isSalesOrLandView"
        >Discover the perfect site for your new home.</span
      >
    </h1>
    <div class="community-details-status">
      <span
        v-if="
          ((selectedDivision.area_code && selectedCommunity.community_code) ||
            !isSalesOrLandView) &&
          initialPlanCount
        "
        >{{ initialPlanCount }} new home plans</span
      >
      <span v-if="showHomeSitesCount"
        >{{ availableSites }} opportunities available</span
      >
    </div>
    <div class="community-details-info">
      <i class="green-circle"></i>
      <span>Tap a photo to see sites on map</span>
    </div>
    <div class="community-details-btngroups">
      <div
        v-if="isSalesOrLandView"
        aria-label="division"
        class="division"
        @click="hideDropDown"
      >
        <label class="input-label">Division</label>
        <button
          class="community-details-btn flexDiv"
          @click="showDivision = !showDivision"
        >
          <span
            v-if="selectedDivision?.division_name"
            class="title-case-text"
            :title="toTitleCase(selectedDivision.division_name)"
            >{{ selectedDivision.division_name.toLowerCase() }}</span
          >
          <span v-else>Division</span>
          <ArrowDownIcon :class="{ 'rotate-180': showDivision }" />
        </button>
        <!-- dropdowns -->
        <div :class="['dropdown scrollbar', { show: showDivision }]">
          <h3 class="homeType-title">Division</h3>
          <ul>
            <li
              class="dropdown-item"
              v-for="division in divisionList"
              :key="division.division_code"
              @click="onDivisionSelected(division)"
            >
              <div class="form-check title-case-text">
                {{ division.division_name.toLowerCase() }}
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div
        v-if="isSalesOrLandView"
        aria-label="community"
        class="community"
        @click="hideDropDown"
      >
        <label class="input-label">Community</label>
        <button
          :disabled="!selectedDivision.area_code"
          class="community-details-btn flexDiv"
          @click="showCommunity = !showCommunity"
        >
          <span
            v-if="selectedCommunity?.community_name"
            class="title-case-text"
            :title="toTitleCase(selectedCommunity.community_name)"
            >{{ selectedCommunity.community_name.toLowerCase() }}</span
          >
          <span v-else>Community</span>
          <ArrowDownIcon :class="{ 'rotate-180': showCommunity }" />
        </button>
        <!-- dropdowns -->
        <div :class="['dropdown scrollbar', { show: showCommunity }]">
          <h3 class="homeType-title">Community</h3>
          <ul>
            <li
              class="dropdown-item"
              v-for="community in communitiesList"
              :key="community.community_code"
              @click="onCommunitySelected(community)"
            >
              <div class="form-check title-case-text">
                {{ community.community_name.toLowerCase() }}
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div
        v-if="isSalesOrLandView"
        aria-label="neighborhood"
        class="neighborhood"
        @click="hideDropDown"
      >
        <label class="input-label">Neighborhood</label>
        <button
          :disabled="
            !selectedDivision.area_code || !selectedCommunity.community_code
          "
          class="community-details-btn flexDiv"
          @click="showNeighborhood = !showNeighborhood"
        >
          <span
            class="title-case-text"
            :title="toTitleCase(selectedNeighborhood.neighborhood_name)"
            v-if="
              selectedNeighborhood && selectedNeighborhood.neighborhood_name
            "
            >{{ selectedNeighborhood.neighborhood_name }}</span
          >
          <span v-else>Neighborhood</span>
          <ArrowDownIcon :class="{ 'rotate-180': showNeighborhood }" />
        </button>
        <!-- dropdowns -->
        <div :class="['dropdown scrollbar', { show: showNeighborhood }]">
          <h3 class="homeType-title">Neighborhood</h3>
          <ul>
            <li
              :class="[
                'dropdown-item',
                {
                  'disabled-item':
                    !neighborhoodLotsCount(nb.id) ||
                    neighborhoodLotsCount(nb.id) <= 0,
                },
              ]"
              v-for="nb in neighborhoods"
              :key="nb.id"
              @click="onNeighborhoodSelected(nb)"
            >
              <div
                :class="[
                  'form-check',
                  {
                    'is-selected':
                      selectedNeighborhoodIds.includes(nb.id) &&
                      !(
                        !neighborhoodLotsCount(nb.id) ||
                        neighborhoodLotsCount(nb.id) <= 0
                      ),
                  },
                  { 'coming-soon': isComingSoon(nb) },
                ]"
              >
                {{ nb.neighborhood_name }}
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div aria-label="price range" class="priceRange" @click="hideDropDown">
        <label v-if="isSalesOrLandView" class="input-label">Price Range</label>
        <button
          :class="[
            { disablePriceRange: conditionShowPriceRange || planPriceExsist },
            'community-details-btn',
            { disableFilter: disableFilters },
          ]"
          @click="togglePriceRangeDropdown"
        >
          <span
            v-if="!minValueAfterChangeOnSlider && !maxValueAfterChangeOnSlider"
            >Price Range</span
          >
          <span v-else>{{
            conditionShowPriceRange || !initialHomePlanCount || planPriceExsist
              ? 'Price Range'
              : setFormatForPriceRange
          }}</span>
          <ArrowDownIcon
            v-if="!conditionShowPriceRange"
            :class="{ 'rotate-180': showPriceRange }"
          />
        </button>
        <div :class="['dropdown', { show: showPriceRange }]">
          <RangeSlider
            v-if="showPriceRange"
            :min="minRangeOnSlider"
            :max="maxRangeOnSlider"
            :showMinRangeOnSlider="showMinRangeOnSlider"
            :showMaxRangeOnSlider="showMaxRangeOnSlider"
            @applyPriceFilter="applyPriceFilter"
            @clearPriceFilter="clearPriceFilter"
            @closePriceRange="closePriceRange"
          />
        </div>
      </div>
      <div
        aria-label="plan name"
        class="planName relative"
        @click="hideDropDown"
      >
        <label v-if="isSalesOrLandView" class="input-label">Plan Name</label>
        <button
          class="community-details-btn flexDiv"
          :class="[{ disableFilter: disableFilters || !initialHomePlanCount }]"
          @click="!disableFilters ? (showPlanName = !showPlanName) : ''"
        >
          <span>Plan Name</span>
          <span v-if="listPlanName?.length > 0 && initialHomePlanCount"
            >({{ listPlanName.length }})</span
          >
          <ArrowDownIcon :class="{ 'rotate-180': showPlanName }" />
        </button>
        <!-- dropdowns -->
        <div :class="['dropdown scrollbar', { show: showPlanName }]">
          <ul>
            <li class="dropdown-item" key="all">
              <div class="form-check">
                <input
                  type="checkbox"
                  id="all"
                  class="form-check-input"
                  :value="allPlansSelected"
                  v-model="allPlansSelected"
                  @click="selectAllPlans"
                />
                <label for="all" class="form-check-label">Select All</label>
              </div>
            </li>
            <li
              class="dropdown-item"
              v-for="planName in planNameQmiList"
              :key="planName.plan_id"
            >
              <div :for="planName.plan_id" class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  v-model="listPlanName"
                  :value="planName.plan_name"
                  :id="planName.plan_id"
                  @click="addListPlanId(planName.plan_id)"
                />
                <label :for="planName.plan_id" class="form-check-label">{{
                  planName.plan_name
                }}</label>
              </div>
            </li>
          </ul>
          <div aria-label="cta buttons" class="cta__btn">
            <button class="cta__btntext" @click="clearPlanName">Clear</button>
            <button
              :class="[
                'cta__btnsolid',
                { disableApplyButton: !listPlanName?.length },
              ]"
              @click="applyPlanNameFilter"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
      <div aria-label="home type" class="homeType" @click="hideDropDown">
        <label v-if="isSalesOrLandView" class="input-label">Home Type</label>
        <button
          class="community-details-btn flexDiv"
          :class="[
            {
              disableFilter:
                disableFilters || !initialHomePlanCount || homeTypeCount < 2,
            },
          ]"
          @click="!disableFilters ? (showHomeType = !showHomeType) : ''"
        >
          <span>Home Type</span>
          <span v-if="listHomeType?.length > 0 && initialHomePlanCount"
            >({{ listHomeType.length }})</span
          >
          <ArrowDownIcon :class="{ 'rotate-180': showHomeType }" />
        </button>
        <!-- dropdowns -->
        <div :class="['dropdown scrollbar', { show: showHomeType }]">
          <h3 class="homeType-title">Home Type</h3>
          <ul>
            <li
              class="dropdown-item"
              v-for="(homeType, index) in homeTypeList"
              :key="index"
            >
              <div :for="index" class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  v-model="listHomeType"
                  :value="homeType"
                  :id="index"
                />
                <label :for="index" class="form-check-label">{{
                  homeType
                }}</label>
              </div>
            </li>
          </ul>
          <div aria-label="cta buttons" class="cta__btn">
            <button class="cta__btntext" @click="clearHomeType">Clear</button>
            <button
              :class="[
                'cta__btnsolid',
                { disableApplyButton: !listHomeType.length },
              ]"
              @click="applyHomeTypeFilter"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  CommunityQmiDetails,
  HeaderData,
  NeighborhoodPlanQmiCounts,
  NeighborhoodSoldDetails,
  QmiDays,
  Sibling,
  SidebarHomePlan,
  planDetails,
} from '@/types';
import ArrowDownIcon from '@/assets/icons/ArrowDown.vue';
import RangeSlider from './modal/RangeSlider.vue';
import {
  HomePlanSortOptions,
  QmiDaysFilter,
  SliderChange,
  OrderHomeTypeList,
  UrlFilterList,
  Settings,
} from '@/constant';
import { compareSortingValue } from '@/core/funcs';
import { isMobile, isTablet } from 'mobile-device-detect';
import { computed, onMounted, reactive, ref, toRefs, watch, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import emitter from '../mitt';
import { DivisionInfo, CommunityInfo } from '@/types';

// injector

const isSalesOrLandView = inject('setSelectionDropdown', ref(false));

const showPlanName = ref(false);
const showPriceRange = ref(false);
const showHomeType = ref(false);
const showDivision = ref(false);
const showCommunity = ref(false);
const showNeighborhood = ref(false);
const selectedDivision = ref({} as DivisionInfo);
const selectedCommunity = ref({} as CommunityInfo);
const selectedNeighborhood = ref({} as any);

const listHomeType = ref<string[]>([]);
const listPlanName = ref<string[]>([]);
const listPlanId = ref<string[]>([]);
// const initialListPlanName = ref<string[]>([])
// const initialHomeType = ref<string[]>([])

// const divisions = ref([] as any[]);
// const communities = ref([] as any[]);

const minRangeOnSlider = ref(0);
const maxRangeOnSlider = ref(0);
const showMinRangeOnSlider = ref(0);
const showMaxRangeOnSlider = ref(0);
const minValueAfterChangeOnSlider = ref(0);
const maxValueAfterChangeOnSlider = ref(0);

const initialMin = ref(Settings.MinValue);
const initialMax = ref(Settings.MaxValue);

const homeTypeList = reactive(new Set<string>());

// const showPlaneNameCount = ref(false)
// const showHomeTypeCount = ref(false)

const disableFilters = ref(false);
const initialPlanCount = ref(0);
const allPlansSelected = ref(false);

const planNameQmiList = ref<planDetails[]>([]);

const homePlanSortOptions = HomePlanSortOptions;
const qmiDaysFilter = QmiDaysFilter;

const hasMobile = ref<boolean>(!!(isMobile && !isTablet));

const urlMaxPrice = ref('');
const urlMinPrice = ref('');

const communitiesList = inject('communitiesList', ref([] as CommunityInfo[]));
const divisionList = inject('divisionList', ref([] as DivisionInfo[]));
// const neighborhoodList = inject("neighborhoodList", ref([]))

const props = defineProps<{
  headerData: HeaderData;
  availableSites: number;
  availablePlans: number;
  qmiAndHomeplans: any[];
  availableLots: number;
  selectedNeighborhoodIds: string[];
  planPriceExsist: boolean;
  qmiDetails: CommunityQmiDetails;
  soldPercentage: NeighborhoodSoldDetails[];
  neighborhoodsPlanQmiCount: NeighborhoodPlanQmiCounts[];
  communityHomePlans: SidebarHomePlan[];
  initialHomePlanCount: number;
  filterQmiCount: number;
  qmiFilter: boolean;
  qmiAppliedMobile: boolean;
  homeTypeCount: number;
  communityName: string;
  areaName: string;
  neighborhoodLotsCount: (neighborhoodId: string) => number;
  isComingSoon: (sibling: Sibling) => boolean;
}>();

const {
  headerData,
  availableSites,
  availablePlans,
  qmiAndHomeplans,
  availableLots,
  selectedNeighborhoodIds,
  planPriceExsist,
  qmiDetails,
  soldPercentage,
  neighborhoodsPlanQmiCount,
  communityHomePlans,
  initialHomePlanCount,
  filterQmiCount,
  qmiFilter,
  qmiAppliedMobile,
  homeTypeCount,
  neighborhoodLotsCount,
  isComingSoon,
  areaName,
  communityName,
} = toRefs(props);

const route = useRoute();
const router = useRouter();

const emit = defineEmits([
  'filterPlanNameHomeType',
  'setSorting',
  'setPlanNameListId',
  'loadNeighborhood',
  'applyUrlQmi',
  'applyMobileFilters',
  'setPlanDetailsHometypeList',
  'applyMobilePriceFilter',
  'selectNeighborhood',
]);

const neighborhoods = computed(() => {
  const siblings = headerData.value?.sibling ?? [];
  if(siblings.length) {
    return [...(siblings)].sort((a, b) =>
      a.neighborhood_name.localeCompare(b.neighborhood_name)
    );
  }
  return [];
});

// "N new home plans" counts the distinct Quick Move-in plans while that filter is
// active, and the community's home plans otherwise. Every writer must go through
// here, or whichever one happens to run last in the flush wins.
const syncPlanCount = () => {
  initialPlanCount.value =
    qmiFilter.value || qmiAppliedMobile.value
      ? filterQmiCount.value
      : availablePlans.value;
};

const mountHandler = () => {
  homeTypeList.clear();
  getHomeTypeList();
  calculateMinMaxPriceRange();
  communityHomePlans.value.sort((field1, field2) =>
    compareSortingValue(field1.plan_name, field2.plan_name)
  );
  setPlanDetailsHometypeList(
    communityHomePlans.value,
    homeTypeList,
    minRangeOnSlider.value,
    maxRangeOnSlider.value
  );

  // mounted()
  syncPlanCount();
  checkFilterConditions();

  const planId: string[] = [];
  planNameQmiList.value = [];
  communityHomePlans.value?.forEach((homePlan) => {
    planNameQmiList.value.push({
      plan_id: homePlan.plan_id,
      plan_name: homePlan.plan_name,
    });
    planId.push(homePlan.plan_id);
  });

  for (const key of Object.keys(qmiDetails.value)) {
    qmiDetails.value[key]?.forEach((qmi) => {
      if (!planId.includes(qmi.plan_code)) {
        planNameQmiList.value.push({
          plan_id: qmi.plan_code,
          plan_name: qmi.plan_market_name,
        });
        planId.push(qmi.plan_code);
      }
    });
  }
  if (!isSalesOrLandView.value) {
    urlQueryFilters();
    urlSortFilter();
  }
};

watch(divisionList, () => {
  divisionList.value = divisionList.value.sort((divisionPrev, divisionNew) =>
    divisionPrev.division_name.localeCompare(divisionNew.division_name)
  );
});

watch(communitiesList, () => {
  communitiesList.value = communitiesList.value
    ? communitiesList.value.sort((communityPrev, communityNew) =>
        communityPrev.community_name.localeCompare(communityNew.community_name)
      )
    : communitiesList.value;
});

onMounted(async () => {
  // selectDivision();
  emitter.on('divisionSelected', onDivisionSelected);
  emitter.on('communitySelected', onCommunityCodeSelected);
  window.addEventListener('click', hideDropDown);
  if (
    !communityHomePlans.value?.length &&
    !Object.keys(qmiDetails.value || {}).length
  ) {
    return;
  }
  mountHandler();
});
const selectCommunity = () => {
  if (
    communityName.value?.toLowerCase() !==
    selectedCommunity.value?.name?.toLowerCase()
  ) {
    selectedCommunity.value = communitiesList.value.find(
      (community) =>
        community.community_name.toLowerCase() ===
        communityName.value.toLowerCase()
    );
  }
};
const selectDivision = () => {
  if (
    areaName.value?.toLowerCase() !==
    selectedDivision.value?.division_name?.toLowerCase()
  ) {
    selectedDivision.value =
      divisionList.value.find((division) => {
        const area = division?.area_link_name;
        return area.toLowerCase() === areaName.value.toLowerCase();
      }) ?? ({} as DivisionInfo);
  }
};
const toTitleCase = (str) =>
  str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

watch(
  [communityHomePlans, qmiDetails],
  ([newCommunityHomePlans, newQmiDetails]) => {
    const hasQmiData = newQmiDetails && Object.keys(newQmiDetails).length > 0;
    const hasHomePlans =
      Array.isArray(newCommunityHomePlans) && newCommunityHomePlans.length > 0;

    if (hasQmiData || hasHomePlans) {
      mountHandler();
      if (isSalesOrLandView.value) {
        clearAllFilters();
      }
    }
  },
  { deep: true }
);

watch(
  selectedDivision,
  async (newVal, oldVal) => {
    if (newVal) {
      emitter.emit('newDivisonSelected', {
        area: selectedDivision.value,
      });
      // will use later

      // const path = selectedDivision.value?.pageUrl;
      // const area = path.split('/')[1];
      // if (!oldVal?.id || newVal.id !== oldVal.id) {
      //   selectedCommunity.value = {} as any;
      //   selectedNeighborhood.value = {} as any;
      //   // await getCommunities();
      //   if (newVal.id && area.toLowerCase() === areaName.value.toLowerCase())
      //     selectCommunity();
      // }
    }
  },
  {
    deep: true,
  }
);

watch(
  selectedCommunity,
  (newVal, oldVal) => {
    if (
      newVal?.community_name?.toLowerCase() !==
      oldVal?.community_name?.toLowerCase()
    ) {
      selectedNeighborhood.value = {} as any;
      planNameQmiList.value = [];
      if (oldVal && newVal.area_id) {
        emitter.emit('communityUpdated', {
          area: selectedDivision.value,
          community: selectedCommunity.value,
        });
      }
    }
  },
  {
    deep: true,
  }
);
const queryFilterExsist = (
  urlFilterData: string[],
  planHomeList: planDetails[] | Set<string>
) => {
  if (Array.isArray(planHomeList)) {
    urlFilterData = urlFilterData.filter((element) =>
      planHomeList.find((data) => data.plan_name === element)
    );
  } else {
    urlFilterData = urlFilterData.filter((element) =>
      (planHomeList as Set<string>).has(element)
    );
  }
  return urlFilterData;
};

const checkPriceInRange = (minPrice: number, maxPrice: number) => {
  if (minPrice >= maxPrice) {
    return true;
  } else if (
    !(minPrice > minRangeOnSlider.value && minPrice < maxRangeOnSlider.value) &&
    !(maxPrice > minRangeOnSlider.value && maxPrice < maxRangeOnSlider.value)
  ) {
    return true;
  }
  return false;
};

const accurateUrlPrice = (minPrice: number, maxPrice: number) => {
  if (checkPriceInRange(minPrice, maxPrice)) {
    minPrice = minRangeOnSlider.value;
    maxPrice = maxRangeOnSlider.value;
  } else if (minPrice % SliderChange || maxPrice % SliderChange) {
    minPrice = parseInt(changeMaxMinPriceRange(minPrice));
    maxPrice = minPrice + SliderChange;
  }
  return {
    minPrice,
    maxPrice,
  };
};

const checkHomeTypeApplicable = () => {
  if (homeTypeCount.value < 2) {
    updateQueryFilter(UrlFilterList.HOME_TYPE);
  }
};
const urlQueryFilters = () => {
  urlMaxPrice.value = route.query[UrlFilterList.MAX_PRICE] as string;
  urlMinPrice.value = route.query[UrlFilterList.MIN_PRICE] as string;

  const filteredHomePlans = route.query[UrlFilterList.PLAN_NAME];
  if (filteredHomePlans && Array.isArray(filteredHomePlans)) {
    listPlanName.value = queryFilterExsist(
      filteredHomePlans as string[],
      planNameQmiList.value
    );
  } else if (filteredHomePlans) {
    listPlanName.value = queryFilterExsist(
      [filteredHomePlans as string],
      planNameQmiList.value
    );
  }

  checkHomeTypeApplicable();

  const filteredHomeTypes = route.query[UrlFilterList.HOME_TYPE];
  if (filteredHomeTypes && Array.isArray(filteredHomeTypes)) {
    listHomeType.value = queryFilterExsist(
      filteredHomeTypes as string[],
      homeTypeList
    );
  } else if (filteredHomeTypes) {
    listHomeType.value = queryFilterExsist(
      [filteredHomeTypes as string],
      homeTypeList
    );
  }

  urlQmiFilter();
};

const urlPriceFilter = () => {
  let correctMinPrice = Settings.MinValue;
  let correctMaxPrice = Settings.MaxValue;

  if (urlMaxPrice.value) {
    correctMaxPrice = parseInt(urlMaxPrice.value);
  }
  if (urlMinPrice.value) {
    correctMinPrice = parseInt(urlMinPrice.value);
  }

  if (
    correctMaxPrice !== Settings.MaxValue &&
    correctMinPrice !== Settings.MinValue &&
    !conditionShowPriceRange.value &&
    !planPriceExsist.value
  ) {
    const correctMinMax = accurateUrlPrice(correctMinPrice, correctMaxPrice);
    initialMin.value = correctMinMax.minPrice;
    initialMax.value = correctMinMax.maxPrice;

    if (
      hasMobile.value &&
      (initialMin.value !== minRangeOnSlider.value ||
        initialMax.value !== maxRangeOnSlider.value)
    ) {
      applyMobilePriceFilter(initialMin.value, initialMax.value);
    }

    applyPriceFilter({
      minValue: initialMin.value,
      maxValue: initialMax.value,
    });
  } else {
    updateQueryFilter(UrlFilterList.MAX_PRICE);
    updateQueryFilter(UrlFilterList.MIN_PRICE);
  }
};
const urlSortFilter = () => {
  const sortType = route.query[UrlFilterList.SORT_BY] as string;
  const sortOption = homePlanSortOptions.find(
    (option) => option.value === sortType
  );

  if (sortOption) {
    setSorting(sortOption);
  } else {
    updateQueryFilter(UrlFilterList.SORT_BY);
  }
};

const urlQmiFilter = () => {
  const qmiList = route.query[UrlFilterList.QMI_FILTER] as string[];
  const qmiDaysFilterList = qmiList
    ? qmiDaysFilter.filter((data) => qmiList.includes(data.value))
    : [];

  if (qmiDaysFilterList.length) {
    applyUrlQmi(qmiDaysFilterList);
  } else {
    updateQueryFilter(UrlFilterList.QMI_FILTER);
  }

  if (
    hasMobile.value &&
    (listPlanName.value.length ||
      listHomeType.value.length ||
      qmiDaysFilterList.length)
  ) {
    applyMobileFilters(
      listPlanName.value,
      listHomeType.value,
      qmiDaysFilterList
    );
  }
};

const updateQueryFilter = (filterType: string) => {
  const newQuery = { ...route.query };
  newQuery[filterType] = [];

  router.push({ query: newQuery }).catch(() => {});
};

const updateQmiFilter = computed(() => {
  return `${qmiFilter.value} ${filterQmiCount.value} ${availablePlans.value} ${qmiAppliedMobile.value}`;
});

watch(updateQmiFilter, syncPlanCount);

watch(homeTypeCount, (newVal) => {
  if (newVal < 2) {
    listHomeType.value = [];
  }
});
watch([listHomeType, listPlanName], () => {
  allPlansSelected.value =
    listPlanName.value.length === planNameQmiList.value.length;

  if (!hasMobile.value) {
    applyFilter();
  }
});

// The count covers every home type, so the only reason to hide it is that there
// is nothing to show (DREESSP-1151).
const showHomeSitesCount = computed(() => availableSites.value > 0);

const conditionShowPriceRange = computed(() => {
  if (!initialHomePlanCount.value) {
    return true;
  }

  if (selectedNeighborhoodIds.value.length) {
    const currentSoldPercentage = soldPercentage.value.filter((element) =>
      selectedNeighborhoodIds.value.includes(element.neighborhoodId)
    );
    return currentSoldPercentage.length
      ? currentSoldPercentage[0].soldPercentage >= 100
      : true;
  } else {
    const currentSoldPercentage = soldPercentage.value.filter(
      (element) => element.soldPercentage < 100
    );
    return !currentSoldPercentage.length;
  }
});

// Emit Events
const filterPlanNameHomeType = (
  listHomeType: string[],
  listPlanName: string[],
  maxPrice: number,
  minPrice: number
) => {
  emit('filterPlanNameHomeType', {
    listHomeType,
    listPlanName,
    maxPrice,
    minPrice,
  });
};
const setSorting = (option: any) => {
  emit('setSorting', option);
};
const setPlanNameListId = (listPlanId: string[]) => {
  emit('setPlanNameListId', listPlanId);
};

const applyUrlQmi = (qmiFilterList: QmiDays[]) => {
  emit('applyUrlQmi', qmiFilterList);
};

const applyMobileFilters = (
  listPlanName: string[],
  listHomeType: string[],
  qmiFilterList: QmiDays[]
) => {
  emit('applyMobileFilters', { listPlanName, listHomeType, qmiFilterList });
};

const setPlanDetailsHometypeList = (
  communityHomePlans: SidebarHomePlan[],
  homeTypeList: Set<string>,
  minPrice: number,
  maxPrice: number
) => {
  emit('setPlanDetailsHometypeList', {
    communityHomePlans,
    homeTypeList,
    minPrice,
    maxPrice,
  });
};

const applyMobilePriceFilter = (minValue: number, maxValue: number) => {
  emit('applyMobilePriceFilter', { minValue, maxValue });
};
watch(selectedNeighborhoodIds, (newVal, oldVal) => {
  if (newVal !== oldVal || !selectedNeighborhood.value?.id) {
    selectedNeighborhood.value =
      neighborhoods.value.find((nb) => newVal.includes(nb.id)) || {};
    checkFilterConditions();
  }
}, {deep: true});
watch(soldPercentage, () => {
  if (!soldPercentage.value?.length) return;
  checkFilterConditions();
  urlPriceFilter();
});
const setFormatForPriceRange = computed(() => {
  const minData = minValueAfterChangeOnSlider.value + '';
  const maxData = maxValueAfterChangeOnSlider.value + '';

  const minDataToShowOnSelectorPanel = minData.substring(0, minData.length - 3);
  const maxDataToShowOnSelectorPanel = maxData.substring(0, maxData.length - 3);

  return `$${minDataToShowOnSelectorPanel}k-$${maxDataToShowOnSelectorPanel}k`;
});

const checkFilterConditions = () => {
  if (selectedNeighborhoodIds.value.length) {
    const selectedNeighborhoodId = selectedNeighborhoodIds.value[0];

    if (
      soldPercentage.value.find(
        (neighborhood) => neighborhood.neighborhoodId === selectedNeighborhoodId
      )?.totalLotsCount
    ) {
      const requiredNeighborhoodData = neighborhoodsPlanQmiCount.value.find(
        (neighborhood) => neighborhood.neighborhoodId === selectedNeighborhoodId
      );

      const planQmisCount = requiredNeighborhoodData
        ? requiredNeighborhoodData.neighborhoodPlansCount +
          requiredNeighborhoodData.neighborhoodQmisCount
        : 0;

      disableFilters.value = !planQmisCount;
    } else {
      disableFilters.value = true;
    }
  } else {
    // NOTE: kept original "!== 0" condition
    const zeroHomePlanQmiCountNeighborhood =
      neighborhoodsPlanQmiCount.value.find(
        (neighborhood) =>
          neighborhood.neighborhoodPlansCount +
            neighborhood.neighborhoodQmisCount !==
          0
      );

    disableFilters.value = !zeroHomePlanQmiCountNeighborhood;

    if (disableFilters.value) {
      const zeroLotCountNeighborhood = soldPercentage.value.find(
        (neighborhood) => neighborhood.totalLotsCount !== 0
      );
      disableFilters.value = !zeroLotCountNeighborhood;
    }
  }
};

const selectAllPlans = () => {
  listPlanId.value = [];
  listPlanName.value = [];

  if (!allPlansSelected.value) {
    planNameQmiList.value?.forEach((plan) => {
      listPlanId.value.push(plan.plan_id);
      listPlanName.value.push(plan.plan_name);
    });
  }

  allPlansSelected.value = !allPlansSelected.value;
};

const applyFilter = () => {
  filterPlanNameHomeType(
    listHomeType.value,
    listPlanName.value,
    initialMax.value,
    initialMin.value
  );
};

const hideDropDown = (e: Event) => {
  const planName = document.querySelector('.planName');
  const priceRange = document.querySelector('.priceRange');
  const homeType = document.querySelector('.homeType');
  const division = document.querySelector('.division');
  const community = document.querySelector('.community');
  const neighborhood = document.querySelector('.neighborhood');

  if (planName && !planName.contains(e.target as Node)) {
    showPlanName.value = false;
    // listPlanName.value = initialListPlanName.value
  }

  if (priceRange && !priceRange.contains(e.target as Node)) {
    showPriceRange.value = false;
  }

  if (homeType && !homeType.contains(e.target as Node)) {
    showHomeType.value = false;
    // listHomeType.value = initialHomeType.value
  }
  if (division && !division.contains(e.target as Node)) {
    showDivision.value = false;
  }
  if (community && !community.contains(e.target as Node)) {
    showCommunity.value = false;
  }
  if (neighborhood && !neighborhood.contains(e.target as Node)) {
    showNeighborhood.value = false;
  }
};
const calculateMinMaxQmiDetails = () => {
  let remainingNeighborhoodQmiMin = Settings.MaxValue;
  let remainingNeighborhoodQmiMax = Settings.MinValue;

  for (const key of Object.keys(qmiDetails.value)) {
    remainingNeighborhoodQmiMin = qmiDetails.value[key].reduce(
      (oldVal, newVal) =>
        newVal.price > 0 ? Math.min(oldVal, newVal.price) : oldVal,
      remainingNeighborhoodQmiMin
    );

    remainingNeighborhoodQmiMax = qmiDetails.value[key].reduce(
      (oldVal, newVal) => Math.max(oldVal, newVal.price),
      remainingNeighborhoodQmiMax
    );
  }

  return {
    min: remainingNeighborhoodQmiMin,
    max: remainingNeighborhoodQmiMax,
  };
};
const calculateMinMaxPriceRange = () => {
  minRangeOnSlider.value = communityHomePlans.value.reduce((oldVal, newVal) => {
    const price = newVal.price_low_with_premium;
    return price > 0 ? Math.min(oldVal, price) : oldVal;
  }, Settings.MaxValue);

  maxRangeOnSlider.value = communityHomePlans.value.reduce(
    (oldVal, newVal) => Math.max(oldVal, newVal.price_low_with_premium),
    Settings.MinValue
  );

  const qmiMinMax = calculateMinMaxQmiDetails();
  minRangeOnSlider.value = Math.min(minRangeOnSlider.value, qmiMinMax.min);
  maxRangeOnSlider.value = Math.max(maxRangeOnSlider.value, qmiMinMax.max);

  minRangeOnSlider.value = parseInt(
    changeMaxMinPriceRange(minRangeOnSlider.value)
  );
  showMinRangeOnSlider.value = minRangeOnSlider.value;

  let maxSliderValueAsPerSetup = minRangeOnSlider.value;
  while (maxSliderValueAsPerSetup < maxRangeOnSlider.value) {
    maxSliderValueAsPerSetup += SliderChange;
  }

  maxRangeOnSlider.value = maxSliderValueAsPerSetup;
  showMaxRangeOnSlider.value = maxRangeOnSlider.value;
};
const addListPlanId = (planId: string) => {
  const index = listPlanId.value.indexOf(planId);
  if (index >= 0) {
    listPlanId.value.splice(index, 1);
  } else {
    listPlanId.value.push(planId);
  }
};
const clearPriceFilter = (payload: { minShow: number; maxShow: number }) => {
  minValueAfterChangeOnSlider.value = payload.minShow;
  maxValueAfterChangeOnSlider.value = payload.maxShow;

  showMinRangeOnSlider.value = minRangeOnSlider.value;
  showMaxRangeOnSlider.value = maxRangeOnSlider.value;

  initialMax.value = Settings.MaxValue;
  initialMin.value = Settings.MinValue;

  applyFilter();
};
const clearPlanName = () => {
  // initialListPlanName.value = []
  listPlanName.value = [];
  listPlanId.value = [];

  setPlanNameListId(listPlanId.value);

  // showPlaneNameCount.value = false
  applyFilter();
};
const clearHomeType = () => {
  // showHomeTypeCount.value = false
  listHomeType.value = [];
  // initialHomeType.value = []
  applyFilter();
};
const applyPriceFilter = (payload: { minValue: number; maxValue: number }) => {
  const { minValue, maxValue } = payload;
  minValueAfterChangeOnSlider.value = minValue;
  maxValueAfterChangeOnSlider.value = maxValue;

  showMinRangeOnSlider.value = minValue;
  initialMin.value = minValue;

  showMaxRangeOnSlider.value = maxValue;
  initialMax.value = maxValue;

  applyFilter();
};

const clearAllFilters = () => {
  clearPriceFilter({ minShow: 0, maxShow: 0 });
  clearHomeType();
  clearPlanName();
};
const closePriceRange = () => {
  showPriceRange.value = false;
};

const applyPlanNameFilter = () => {
  if (listPlanName.value.length) {
    // initialListPlanName.value = listPlanName.value
    showPlanName.value = false;
    // setPlanNameListId(listPlanId.value)
    // showPlaneNameCount.value = true
    // applyFilter()
  }
};
const applyHomeTypeFilter = () => {
  if (listHomeType.value.length) {
    // initialHomeType.value = listHomeType.value
    // showHomeTypeCount.value = true
    showHomeType.value = false;
    // applyFilter()
  }
};
const changeMaxMinPriceRange = (minValue: number) => {
  const stringMinMaxValue = minValue + '';
  let changedMinRangeSliderAsPerStep: string;

  if (stringMinMaxValue.length <= 6) {
    +stringMinMaxValue.substring(1, 2) >= 5
      ? (changedMinRangeSliderAsPerStep =
          stringMinMaxValue.substring(0, 1) +
          '5' +
          '0'.repeat((minRangeOnSlider.value + '').length - 2))
      : (changedMinRangeSliderAsPerStep =
          stringMinMaxValue.substring(0, 1) +
          '0'.repeat(stringMinMaxValue.length - 1));
  } else {
    +stringMinMaxValue.substring(2, 3) >= 5
      ? (changedMinRangeSliderAsPerStep =
          stringMinMaxValue.substring(0, 2) +
          '5' +
          '0'.repeat(stringMinMaxValue.length - 3))
      : (changedMinRangeSliderAsPerStep =
          stringMinMaxValue.substring(0, 2) +
          '0'.repeat(stringMinMaxValue.length - 2));
  }

  return changedMinRangeSliderAsPerStep;
};

const sortHomeTypeList = () => {
  const sortedHomeTypeArray: string[] = [];
  for (const type of OrderHomeTypeList) {
    if (homeTypeList.has(type)) {
      sortedHomeTypeArray.push(type);
    }
  }
  homeTypeList.clear();
  for (const type of sortedHomeTypeArray) {
    homeTypeList.add(type);
  }
};


const getHomeTypeList = () => {
  communityHomePlans.value?.forEach((homePlan) => {
    homeTypeList.add(homePlan.plan_type);
  });

  Object.keys(qmiDetails.value)?.forEach((key) => {
    qmiDetails.value[key]?.forEach((eachQmi) => {
      homeTypeList.add(eachQmi.plan_type);
    });
  });

  sortHomeTypeList();
};
defineExpose({
  clearAllFilters,
});

const onDivisionSelected = (division: DivisionInfo) => {
  if(selectedDivision.value.division_code !== division.division_code) {
    selectedDivision.value = division;
    selectedCommunity.value = {} as CommunityInfo;
    selectedNeighborhood.value = {} as any;
  }
  showDivision.value = false;
};

const onCommunityCodeSelected = (community: string) => {
  const selectedCommunity = communitiesList.value.find((comm) => comm.community_code === community);
  if (selectedCommunity) {
    onCommunitySelected(selectedCommunity);
  }
};

const onCommunitySelected = (community: CommunityInfo) => {
  if(selectedCommunity.value.community_code !== community.community_code) {
    selectedCommunity.value = community;
  }
  showCommunity.value = false;
};
const onNeighborhoodSelected = (nb: any) => {
  if(selectedNeighborhood.value.id !== nb.id) {
    selectedNeighborhood.value = nb;
    emit('selectNeighborhood', nb.id);
  }
  showNeighborhood.value = false;
};

const togglePriceRangeDropdown = () => {
  conditionShowPriceRange.value || disableFilters.value
    ? (showPriceRange.value = false)
    : (showPriceRange.value = !showPriceRange.value);
};
</script>
<style lang="scss">
.disabled-item {
  pointer-events: none;
  opacity: 0.5;
}
.community-details {
  padding-bottom: 24px;
  padding-top: 10px;
  padding-inline: 8px;

  @include md {
    padding-bottom: 30px;
  }

  & > :not([hidden]) ~ :not([hidden]) {
    margin-top: 10px;
  }

  &-title {
    @include font(14px, 600);

    @include md {
      @include font(16px, 600);
    }

    color: $white;
    text-align: center;

    @include md {
      text-align: left;
    }

    display: block;
  }

  &-text {
    @include font(11px, 400);
    margin-top: 8px;

    @include md {
      @include font(12px, 400);
      margin-top: 6px;
    }

    color: $white;
    text-align: center;

    @include md {
      text-align: left;
    }

    display: block;
  }

  &-status {
    margin-top: 8px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 10px;
    color: $lime-green-new;
    @include font(12.5px, 600);

    @include md {
      justify-content: flex-start;
      margin-top: 6px !important;
    }

    span:not(:last-child) {
      border-right: 1px solid $white;
      padding-right: 10px;
    }
  }

  &-info {
    display: none;
    align-items: center;
    color: $white;
    column-gap: 8px;
    font-size: 11px;

    @include md {
      display: flex;
    }
  }

  &-btngroups {
    display: none;
    flex-wrap: wrap;
    position: relative;
    gap: 4px;

    @include md {
      display: grid;
      grid-template-columns: repeat(3, 33.3%);
      width: calc(100% - 8px);
    }

    > div {
      flex: 1;
    }

    .btn-clr {
      margin-left: 6px;
    }
  }

  &-btn {
    border: 0;
    outline: 0;
    box-shadow: none;
    padding: 4px 8px;
    background: $beige-700;
    border-radius: 5px;
    @include font(12px, 400);
    line-height: 15px;
    cursor: pointer;
    width: 100%;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: $gray-600;

    &:disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    svg:not(.rotate-180) {
      margin-top: 1px;
    }
  }

  .disablePriceRange {
    pointer-events: none;
    opacity: 0.5;
  }

  .disableFilter {
    pointer-events: none;
    opacity: 0.5;
  }

  .planName {
    .dropdown {
      padding-bottom: 0;
      max-height: 225px;

      ul {
        margin-bottom: 10px;
      }
    }

    .cta__btn {
      position: sticky;
      bottom: 0;
      background: $white;
    }
  }

  .priceRange {
    .dropdown {
      max-width: 249px;
    }
  }

  .homeType {
    .dropdown {
      max-width: 303px;
      right: 0;
      left: auto;

      ul {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
      }
    }

    &-title {
      color: $gray-600;
      text-shadow: 0px 2px 3px 0px rgba(163, 131, 63, 0.13);
      @include font(12px, 700);
      padding: 15px 12px 7px 12px;
    }
  }
}

// comman cta for dropdowns
.cta {
  &__btn {
    &:not(.rangeslider__ctabtn) {
      padding: 14px;
    }

    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: 12px;

    &text,
    &solid {
      border: none;
      outline: 0;
      cursor: pointer;
    }

    &text {
      @include font(10px, 700);
      background: transparent;
      text-transform: capitalize;
      color: $lime-green-new;
    }

    &solid {
      @include font(10px, 400);
      background: $lime-green-new;
      color: $white;
      border-radius: 4px;
      padding: 5px 10px;
      text-align: center;
    }
  }
}

.disableApplyButton {
  cursor: auto;
  opacity: 0.6;
}

.title-case-text {
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: calc(100% - 14px);
}

.is-open {
  .community-details {
    &-title {
      text-align: left;
    }

    &-text {
      margin-top: 10px;
      text-align: left;
    }

    &-status {
      justify-content: flex-start;
      margin-top: 10px !important;
    }
  }
}
.input-label {
  color: $white;
  font-size: 11px;
  line-height: 1.2;
  font-weight: 400;
  display: block;
  margin-bottom: 4px;
}
.sales-view{
  .community-details-btn{
    font-size: 11.5px;
  }
}
</style>
